import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl, MenuItem, Select, type SxProps, type Theme, useTheme } from '@mui/material';
import InputBase from '@mui/material/InputBase';
import type { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { styled } from '@mui/material/styles';

export interface DropDownProps<T> {
    items: { title: string; value: T; icon?: string; hidden?: boolean }[];
    onChange: (value: T) => void;
    onClick?: () => void;
    defaultValue?: T | null;
    input?: React.FC;
    fullWidth?: boolean;
    disabled?: boolean;
    showSelected?: boolean;
    MenuProps?: {
        sx: SxProps<Theme>;
    };
    inputProps?: {
        sx: SxProps<Theme>;
    };
    slotProps?: {
        root?: {
            sx: SxProps<Theme>;
        };
        input?: {
            sx: SxProps<Theme>;
        };
    };
    sx?: SxProps<Theme>;
    placeholder?: string;
    iconTitle?: boolean;
}

const SelectInput = styled(InputBase)(({ theme }) => ({
    '& .MuiInputBase-input': {
        borderRadius: '16px',
        lineHeight: '1.4',
        position: 'relative',
        border: '1px solid #c7cbe01a',
        fontSize: '1.25rem',
        padding: '13px 24px',
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.custom.c27,
        maxWidth: '100%',
        '&:focus': {
            borderRadius: 16,
            borderColor: '#c7cbe01a',
            backgroundColor: theme.palette.secondary.light,
        },
    },
    '& .MuiSvgIcon-root': {
        color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.custom.c27,
        transform: 'rotate(-90deg)',
        width: '0.5em',
        height: '0.5em',
        right: '0.6em',
        top: '0.8em',
    },
}));

export function DropDown<T extends string | number>(props: DropDownProps<T>) {
    const {
        items,
        input: Input = SelectInput,
        MenuProps = {},
        inputProps = {},
        sx = {},
        fullWidth = true,
        disabled = false,
        defaultValue = undefined,
        onChange,
        onClick,
        showSelected,
        placeholder = '',
        iconTitle = false,
        ...rest
    } = props;

    const theme = useTheme();

    const handleChange = (event: SelectChangeEvent<T | string>) => {
        onChange(event.target.value as unknown as T);
    };

    return (
        <FormControl variant="standard" fullWidth={fullWidth}>
            <Select<T | string>
                IconComponent={ArrowBackIosNewIcon}
                value={defaultValue ?? ''}
                disabled={disabled}
                onChange={handleChange}
                input={<Input sx={sx} />}
                fullWidth={fullWidth}
                MenuProps={MenuProps}
                inputProps={inputProps}
                displayEmpty={!!placeholder}
                renderValue={value => {
                    const hasValue = items.some(i => i.value === value);
                    const currentItem = hasValue && items.find(i => i.value === value);

                    return value !== 'none' && hasValue && currentItem ? (
                        <div className="flex flex-wrap w-full items-center">
                            {iconTitle && currentItem.icon && (
                                <Box
                                    component="img"
                                    sx={{ verticalAlign: 'middle', height: '1em' }}
                                    mr="10px"
                                    alt={currentItem.title}
                                    src={currentItem.icon}
                                />
                            )}
                            {currentItem.title}
                        </div>
                    ) : (
                        <Box
                            sx={{
                                color: theme => theme.custom.c3,
                                fontSize: '14px',
                                lineHeight: '24px',
                            }}
                        >
                            {placeholder}
                        </Box>
                    );
                }}
                {...rest}
            >
                {placeholder && (
                    <MenuItem sx={{ display: 'none' }} disabled value={'none'}>
                        {placeholder}
                    </MenuItem>
                )}
                {items.map(item => (
                    <MenuItem
                        key={item.value}
                        value={item.value}
                        onClick={onClick}
                        sx={[
                            !!item.hidden && {
                                display: 'none',
                            },
                        ]}
                    >
                        {item.icon && <Box component="img" sx={{ verticalAlign: 'middle' }} mr="10px" alt={item.title} src={item.icon} />}
                        {item.title}
                        {showSelected && item.value === Number(defaultValue) && (
                            <CheckIcon
                                sx={{
                                    fontSize: 'inherit',
                                    color: theme.palette.secondary.main,
                                }}
                            />
                        )}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
